import SEO from '../../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Download, Play, CheckCircle2, TrendingUp, Package, TruckIcon, BarChart3, Clock, Users, Award, Target, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ProductCTA from '../../components/ProductCTA';
import PersonaNavigation from '../../components/PersonaNavigation';

const OperationsDirectorPage = () => {
  const stats = [
    { value: "99%+", label: "Process Efficiency" },
    { value: "50%", label: "Cost Reduction" },
    { value: "Real-time", label: "Visibility" },
    { value: "End-to-End", label: "Traceability" }
  ];

  const benefits = [
    { icon: Package, title: "Complete Inventory", strapline: "Real-time stock", desc: "Multi-warehouse management with bin locations and automated replenishment." },
    { icon: TruckIcon, title: "Supply Chain", strapline: "End-to-end visibility", desc: "Complete control from suppliers to customer delivery." },
    { icon: BarChart3, title: "Operations Analytics", strapline: "Data-driven", desc: "Real-time dashboards showing KPIs, bottlenecks, and efficiency." },
    { icon: Clock, title: "Process Automation", strapline: "Eliminate manual tasks", desc: "Automate ordering, invoicing, and workflows." },
    { icon: Users, title: "Cross-Functional", strapline: "Break silos", desc: "Connect all departments in one system." },
    { icon: TrendingUp, title: "Continuous Improvement", strapline: "Optimise", desc: "Track performance and implement improvements." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SAP Business One for Operations Directors | Streamline & Optimize"
        description="Maximize efficiency with SAP B1. Automated workflows, real-time inventory, cross-department visibility. Eliminate bottlenecks, drive operational excellence."
        keywords="operations director ERP, workflow automation, operational efficiency, inventory management, SAP B1 operations"
        canonical="https://www.ascarii.com/sap-business-one/for/operations-directors"
      />

      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-orange-700 via-amber-800 to-orange-900">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&q=85" alt="Operations" className="w-full h-full object-cover opacity-30" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">For Operations Directors</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">Streamline Operations, Maximise Efficiency</h1>
              <p className="text-xl md:text-2xl text-orange-100 mb-8 leading-relaxed">Complete supply chain visibility, automated processes, and real-time control.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-orange-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Persona Navigation */}
      <PersonaNavigation currentPersona="operations-directors" />

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4 w-fit">Better Information</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Better Information, Quicker Decisions</h2>
              <p className="text-lg text-gray-600 mb-4">
                If you can receive better information quicker, you will be able to improve key operational areas.
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-teal-50 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">Reduce Stock Levels</h4>
                  <p className="text-sm text-gray-700">Optimise inventory to assist cash flow.</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">Enhance OTIF Delivery</h4>
                  <p className="text-sm text-gray-700">Improve on-time-in-full percentages to enhance customer satisfaction and retention.</p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1592085198739-ffcad7f36b54?w=800&q=85" alt="Supply Chain" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-teal-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-orange-800 via-amber-900 to-orange-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Operations Directors Choose SAP Business One</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-lg text-cyan-300 mb-3 font-medium">{benefit.strapline}</p>
                    <p className="text-orange-100 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-100 via-orange-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Transform Your Operations</h2>
              <p className="text-xl text-gray-600 mb-8">Join COOs achieving 50% cost reduction</p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/10 to-amber-500/10 flex items-center justify-center">
                    <Target className="w-7 h-7 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Operational Excellence</h4>
                    <p className="text-gray-600 text-sm">15+ years expertise</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/10 to-amber-500/10 flex items-center justify-center">
                    <Award className="w-7 h-7 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Proven ROI</h4>
                    <p className="text-gray-600 text-sm">50% cost savings</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-3">
              <ProductCTA productName="SAP Business One for Operations Directors" brandColor="orange-600" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OperationsDirectorPage;
